<template>
<section id="pricing" class="section pricing-two bg-img">
    <div class="container">
        <div class="row justify-content-center">
            <div class="col-12 col-xl-6">
                <div class="section__header wow fadeInUp" data-wow-duration="600ms" data-wow-delay="300ms">
                    <h2 class="h2">{{ labelsState['ready_to_start'] || '¿Ready to start?' }}</h2>
                    <h2 class="h2">{{ labelsState['start_today_free'] || 'Start today totally free' }}</h2>
                </div>
            </div>
        </div>
        <div class="row justify-content-center mb-4">
            <div class="col-auto">
                <button 
                    class="btn btn--filter" 
                    :class="{ active: filter === 'all' }" 
                    @click="filter = 'all'; applyFilter();">
                    {{ labelsState['all_plans'] || 'All Plans' }}
                </button>
                <button 
                    class="btn btn--filter" 
                    :class="{ active: filter === 'free' }" 
                    @click="filter = 'free'; applyFilter();">
                    {{ labelsState['free'] || 'Free' }}
                </button>
                <button 
                    class="btn btn--filter" 
                    :class="{ active: filter === 'monthly' }" 
                    @click="filter = 'monthly'; applyFilter();">
                    {{ labelsState['monthly'] || 'Monthly' }}
                </button>
                <button 
                    class="btn btn--filter" 
                    :class="{ active: filter === 'annual' }" 
                    @click="filter = 'annual'; applyFilter();">
                    {{ labelsState['annual'] || 'Annual' }}
                </button>
                <button
                    class="btn btn--filter"
                    :class="{ active: filter === 'enterprise' }"
                    @click="filter = 'enterprise'; applyFilter();">
                    {{ labelsState['enterprise'] || 'Enterprise' }}
                </button>
                <button
                    class="btn btn--filter"
                    :class="{ active: filter === 'infrastructure' }"
                    @click="filter = 'infrastructure'; applyFilter();">
                    {{ labelsState['infrastructure'] || 'Infrastructure' }}
                </button>
            </div>
        </div>
        <div class="row items-gap justify-content-center">
            <div 
                class="col-12 col-md-6 col-lg-6 col-xxl-4" 
                v-for="plan in filteredPlans" 
                :key="plan.id">
                <div class="pricing-two__single wow fadeInUp" 
     :class="{ 'highlighted-plan': plan.is_highlighted }" 
     data-wow-duration="600ms" 
     data-wow-delay="900ms">
                    <div class="title-header">
                        <h5 class="h5 yel plan-title">{{ plan.subscription_plan }}</h5>
                        <span class="frequency-badge">{{ plan.frequency }} {{ labelsState['month_s'] || 'Month(s)' }}</span>
                    </div>
                    <div class="meta">
                        <div class="content">
                            <!-- USD Price -->
                            <div class="price-row">
                                <div class="price-container">
                                    <h2 class="price discount-price" v-if="plan.discount_amount_usd">
                                        ${{ formatNumber(plan.discount_amount_usd) }}
                                    </h2>
                                    <h2 class="price" :class="{ 'discounted': plan.discount_amount_usd }">
                                        {{ plan.amount ? '$' + formatNumber(plan.amount) : 'Free' }} 
                                        <sup class="cop-super">USD</sup>
                                    </h2>
                                </div>
                                <span class="tax-inline" v-if="plan.amount">+ {{ labelsState['tax'] || 'taxes' }}</span>
                            </div>
                            
                            <!-- COP Price -->
                            <div class="price-row">
                                <div class="price-container">
                                    <h2 class="price discount-price" v-if="plan.discount_amount_cop">
                                        ${{ formatNumber(plan.discount_amount_cop) }}
                                    </h2>
                                    <h2 class="price" :class="{ 'discounted': plan.discount_amount_cop }">
                                        {{ plan.amount_cop ? '$' + formatNumber(plan.amount_cop) : 'Free' }} 
                                        <sup class="cop-super">COP</sup>
                                    </h2>
                                </div>
                                <span class="tax-inline" v-if="plan.amount_cop">+ {{ labelsState['tax'] || 'taxes' }}</span>
                            </div>
                        </div>
                    </div>
                    
                    <hr v-if="plan.credits > 0">
                    <div class="credits-highlight" v-if="plan.credits > 0">
                        <i class="fa-solid fa-coins"></i>
                        <span class="credits-number">{{ formatNumber(plan.credits) }}</span>
                        <span class="credits-label">{{ labelsState['credits'] || 'credits' }}</span>
                    </div>
                    <hr v-if="plan.credits > 0">
                    <p class="text-white-dark mt-2" v-html="plan.description"></p>
                    <hr>
                    <a 
                        :href="subsUrl"
                        target="_blank" 
                        class="btn btn--septenary">
                        {{ plan.is_free ? (labelsState['free_demo'] || 'Free Demo') : (labelsState['suscribe_now'] || 'Subscribe Now') }}
                    </a>
                </div>
            </div>
        </div>
    </div>
</section>
</template>

<script>
import { useLabelsStore } from '@/stores/labels';
import { useLanguageStore } from '@/stores/language';
import { computed, onMounted, ref } from 'vue';
import axios from 'axios';

export default {
    name: "Pricing",
    setup() {
        const subsUrl = `${process.env.VUE_APP_DASHBOARD_URL}/subscriptions`;
        const labelsStore = useLabelsStore();
        const languageStore = useLanguageStore();
        const plans = ref([]);
        const filteredPlans = ref([]);
        const filter = ref("all"); 

        const fetchPlans = async () => {
            try {
                const response = await axios.get(`${process.env.VUE_APP_HOST_BILLING}/plans_open`);
                plans.value = response.data.plans.sort((a, b) => {
                    if (a.is_free && !b.is_free) return -1;
                    if (!a.is_free && b.is_free) return 1;

                    if (a.is_enterprise && !b.is_enterprise) return 1;
                    if (!a.is_enterprise && b.is_enterprise) return -1;

                    if (a.is_infrastructure && !b.is_infrastructure) return 1;
                    if (!a.is_infrastructure && b.is_infrastructure) return -1;

                    if (a.frequency !== b.frequency) {return a.frequency - b.frequency;}
                    if (a.amount !== b.amount) {return (a.amount || 0) - (b.amount || 0);}
                    return 0;
                });
                applyFilter();
    
            } catch (error) {
                console.error("Error fetching plans:", error);
            }
        };

        const applyFilter = () => {
            let filtered = [];
            if (filter.value === "all") {
                filtered = plans.value;
            } else if (filter.value === "free") {
                filtered = plans.value.filter(plan => plan.is_free);
            } else if (filter.value === "monthly") {
                filtered = plans.value.filter(
                    plan => plan.frequency === 1 && !plan.is_free && !plan.is_enterprise && !plan.is_infrastructure
                );
            } else if (filter.value === "annual") {
                filtered = plans.value.filter(
                    plan => plan.frequency === 12 && !plan.is_free && !plan.is_enterprise && !plan.is_infrastructure
                );
            } else if (filter.value === "enterprise") {
                filtered = plans.value.filter(plan => plan.is_enterprise);
            } else if (filter.value === "infrastructure") {
                filtered = plans.value.filter(plan => plan.is_infrastructure);
            }
            
            filteredPlans.value = filtered.sort((a, b) => {
                const aHasCredits = a.credits && a.credits > 0;
                const bHasCredits = b.credits && b.credits > 0;
                if (aHasCredits && !bHasCredits) return -1;
                if (!aHasCredits && bHasCredits) return 1;
                return 0;
            });
        };

        onMounted(() => {
            fetchPlans();
        });

        const labelsState = computed(() => labelsStore.labels);
        const languageCodes = computed(() => languageStore.codes);
        
        function formatNumber(value) {
            if (value === null || value === undefined) return '0';
            return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ".");
        }

        return {
            labelsState,
            languageCodes,
            plans,
            filteredPlans,
            subsUrl,
            filter,
            applyFilter,
            formatNumber,
        };
    },
};
</script>

<style>

.price-container {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;
}

.discount-price {
    text-decoration: line-through;
    color: #9ca3af !important;
    font-size: 1.2rem !important;
    opacity: 0.7;
}

.price.discounted {
    color: #00CC99 !important;
    font-weight: 900 !important;
}

.pricing-two__single.highlighted-plan {
    border: 3px solid #00CC99;
    box-shadow: 0 0 30px rgba(0, 204, 153, 0.4);
    transform: scale(1.02);
    position: relative;
}

.pricing-two__single.highlighted-plan::before {
    content: "⭐ POPULAR";
    position: absolute;
    top: -15px;
    right: 20px;
    background: linear-gradient(90deg, #00CC99 0%, #003366 100%);
    color: white;
    padding: 5px 15px;
    border-radius: 20px;
    font-size: 0.75rem;
    font-weight: bold;
    letter-spacing: 1px;
}

.pricing-two__single.highlighted-plan:hover {
    box-shadow: 0 0 40px rgba(0, 204, 153, 0.6);
}

.pricing-two {
    background-color: #111827;
}

.text-center {
    text-align: center;
}

.title-header {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    text-align: center;
    margin-bottom: 0rem;
    padding: 0.5rem 0;
}

.plan-title {
    font-size: 1.8rem;
    font-weight: bold;
    text-transform: uppercase;
    letter-spacing: 1px;
}

.frequency-badge {
    font-size: 0.7rem;
    color: #9ca3af;
    background: rgba(156, 163, 175, 0.1);
    padding: 0.2rem 0.2rem;
    border-radius: 4px;
}

.price-row {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 0.5rem;
}

.price {
    font-size: 1.5rem;
    margin: 0;
    font-weight: bold;
    color: white;
}

.tax-inline {
    font-size: 0.65rem;
    color: white;
    opacity: 0.6;
}

.meta {
    flex-direction: column;
}

.cop-super {
    font-size: 0.5em;
    vertical-align: super;
    color: white;
}

.meta .content {
    flex-direction: column;
}

.credits-highlight {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 1rem;
    background: linear-gradient(135deg, rgba(0, 204, 153, 0.1) 0%, rgba(0, 51, 102, 0.1) 100%);
    border-radius: 10px;
   
}

.credits-highlight i {
    font-size: 2rem;
    color: #00CC99;
    margin-bottom: 0.3rem;
}

.credits-number {
    font-size: 2.5rem;
    font-weight: bold;
    color: #00CC99;
    line-height: 1;
    margin: 0.2rem 0;
}

.credits-label {
    font-size: 0.85rem;
    color: #d1d5db;
    text-transform: uppercase;
    letter-spacing: 1px;
    margin-top: 0.2rem;
    transition: color 0.3s ease;
}

.pricing-two__single:hover .credits-number {
    color: white;
}

ul {
    list-style: none;
    padding: 0;
    color: white;
}

ul li {
    margin: 0.5rem 0;
}

h2.price {
    display: block;
    text-align: center;
    margin: 0 auto;
}

.pricing-two__single {
    padding: 15px;
    background-color: #1f2937;
    border-radius: 10px;
    height: 100%;
}

.pricing-two__single hr {
    margin: 0.5rem 0;
}

.pricing-two__single .mt-2 {
    margin-top: 0.5rem;
}

.btn--filter {
    padding: 10px 20px;
    margin: 5px;
    border: 1px solid #00CC99;
    color: #00CC99;
    background: transparent;
    cursor: pointer;
    transition: background-color 0.3s ease, color 0.3s ease;
}

.btn--filter:hover {
    background-image: linear-gradient(90deg, #00CC99 0%, #003366 95.62%);
    color: white;
}

.btn--filter.active {
    background-color: #00CC99;
    color: #fff;
    font-weight: bold; 
    border: 1px solid #00CC99;
}

.text-white-dark strong {
    transition: color 0.3s ease;
}

.pricing-two__single:hover .text-white-dark strong {
    color: white !important;
}
</style>